(function (window, document) {
  'use strict';

  if (window.EDSLogoCanvasBootFlash) return;

  var STORAGE_KEY = 'eds-logo-booted';
  var FOOTER_STYLE_ID = 'eds-footer-canvas-logo-style';

  var DEFAULT_ACCENT = '#4AE068';
  var DEFAULT_INK = '#FFFFFF';
  var DEFAULT_METAL = '#767D78';
  var DEFAULT_SLAT = '#05150D';
  var OUTLINE_INK = '#ECEFEA';
  var FACE_FILL = '#090B09';
  var SCREEN_TOP = '#06160E';
  var SCREEN_BOTTOM = '#03100A';

  // Mark geometry. Carried over unchanged from the original build. Editing any
  // of these will desync the header logo from the footer logo.
  var FACE_RATIO = 0.54;
  var RING_RATIO = 0.17;
  var GLYPH_RATIO = 0.66;
  var ARC_START = -0.3 * Math.PI;
  var ARC_END = 1.3 * Math.PI;

  var BLADE_OFFSETS = [-0.32, 0, 0.32];
  var BLADE_LENGTH_RATIO = 1.7;
  var BLADE_STAGGER = 0.055;

  var DEFAULTS = {
    buttonMs: 1400,
    holdMs: 550,
    travelMs: 4200,
    leadIn: 0.06,
    lines: 22,
    pitch: 0.03,
    duty: 0.94,
    dutyTail: 0.1,
    chevron: 0.45,
    height: 0.8,
    slatAlpha: 1,
    trail: 0.85,
    buttonClear: 1.3,
    feather: 0.02,
    outlineAlpha: 0.4,
    outlineWidth: 0.04,
    powerStrokeStart: 0.055,
    powerStrokeEnd: 0.095,
    powerSettle: 0.2,
    wipeMs: 380,
    staggerMs: 115,
    barWidth: 0.28,
    barGap: 0.52,
    taperEnd: 0.42,
    heightTaper: 0.05,
    bars: 3
  };

  function getAttribute(element, name, fallback) {
    if (!element) return fallback;
    var value = element.getAttribute(name);
    return value === null || value === '' || value === undefined ? fallback : value;
  }

  function getNumber(element, name, fallback) {
    var raw = getAttribute(element, name, '');
    var value = Number(raw);
    return raw !== '' && isFinite(value) ? value : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function easeInOutCubic(value) {
    value = clamp(value, 0, 1);
    return value < 0.5
      ? 4 * value * value * value
      : 1 - Math.pow(-2 * value + 2, 3) / 2;
  }

  function smoothstep(value) {
    value = clamp(value, 0, 1);
    return value * value * (3 - 2 * value);
  }

  function hexToRgb(hex) {
    var source = String(hex || '').trim().replace('#', '');
    if (source.length === 3) source = source.replace(/./g, '$&$&');
    return [
      parseInt(source.slice(0, 2), 16) || 0,
      parseInt(source.slice(2, 4), 16) || 0,
      parseInt(source.slice(4, 6), 16) || 0
    ];
  }

  function mixColor(colorA, colorB, amount) {
    var a = hexToRgb(colorA);
    var b = hexToRgb(colorB);
    return (
      'rgb(' +
      [0, 1, 2]
        .map(function (index) {
          return Math.round(a[index] + (b[index] - a[index]) * amount);
        })
        .join(',') +
      ')'
    );
  }

  function rgba(hex, alpha) {
    var c = hexToRgb(hex);
    return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + alpha + ')';
  }

  function LogoCanvas(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-canvas]');
    this.context = this.canvas && this.canvas.getContext('2d');
    if (!this.context) return;

    this.text = getAttribute(root, 'data-words', 'ECO,DRIVE,SMART')
      .replace(/,/g, '')
      .toUpperCase();
    this.powerIndex = Math.max(0, this.text.indexOf('O'));
    this.capHeight = getNumber(root, 'data-cap', 25);
    this.fontFamily = getAttribute(root, 'data-font', 'Unbounded');
    this.fontWeight = getAttribute(root, 'data-font-weight', '700');
    this.accent = getAttribute(root, 'data-accent', DEFAULT_ACCENT);
    this.metal = getAttribute(root, 'data-metal', DEFAULT_METAL);
    this.slatColor = getAttribute(root, 'data-slat-color', DEFAULT_SLAT);
    this.transparent = getAttribute(root, 'data-surface', 'screen') === 'transparent';
    this.adaptiveInk = getAttribute(root, 'data-adaptive-ink', 'false') === 'true';
    this.skipBoot = getAttribute(root, 'data-skip-boot', 'false') === 'true';
    this.enabled = getAttribute(root, 'data-enable-boot-anim', 'true') !== 'false';
    this.frequency = getAttribute(root, 'data-boot-freq', 'load');

    // 'chevron' is the conveyor pass signed off with the client: a run of
    // tapering chevron slats that resolves letters at its leading edge.
    // 'slats' and 'blades' are the earlier green reveals, kept so existing
    // section settings keep working.
    var style = getAttribute(root, 'data-belt-style', 'chevron');
    this.style = style === 'slats' || style === 'blades' ? style : 'chevron';

    this.readTiming();

    this.pixelRatio = 1;
    this.raf = 0;
    this.running = false;

    // Until the first render lands, nothing may paint the finished state. A
    // ResizeObserver fires once on observe(), and without this guard that
    // initial callback drew the completed logo before the boot even started.
    this.primed = false;
  }

  LogoCanvas.prototype.readTiming = function () {
    var root = this.root;
    var t = {};
    var key;

    for (key in DEFAULTS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS, key)) {
        t[key] = DEFAULTS[key];
      }
    }

    t.buttonMs = getNumber(root, 'data-button-ms', t.buttonMs);
    t.holdMs = getNumber(root, 'data-hold-ms', t.holdMs);
    t.travelMs = getNumber(root, 'data-travel-ms', t.travelMs);

    t.lines = clamp(Math.round(getNumber(root, 'data-lines', t.lines)), 1, 60);
    t.pitch = Math.max(0.004, getNumber(root, 'data-pitch', t.pitch * 100) / 100);
    t.duty = clamp(getNumber(root, 'data-duty', t.duty * 100) / 100, 0.05, 1);
    t.dutyTail = clamp(getNumber(root, 'data-duty-tail', t.dutyTail * 100) / 100, 0.02, 1);
    t.chevron = Math.max(0, getNumber(root, 'data-chevron', t.chevron * 100) / 100);
    t.trail = clamp(getNumber(root, 'data-trail', t.trail * 100) / 100, 0, 1);
    t.outlineAlpha = clamp(
      getNumber(root, 'data-outline-alpha', t.outlineAlpha * 100) / 100,
      0,
      1
    );

    t.wipeMs = getNumber(root, 'data-wipe-ms', t.wipeMs);
    t.staggerMs = getNumber(root, 'data-stagger-ms', t.staggerMs);
    t.barWidth = getNumber(root, 'data-bar-width', t.barWidth);
    t.taperEnd = clamp(getNumber(root, 'data-bar-taper', t.taperEnd), 0.02, 1);
    t.bars = clamp(Math.round(getNumber(root, 'data-bar-count', t.bars)), 1, 6);

    this.timing = t;

    // data-boot is the legacy whole sequence duration in seconds. Honour it
    // only when no explicit millisecond values were supplied, so existing
    // sections keep the pacing their settings imply.
    var legacy = getNumber(root, 'data-boot', 0);
    var explicit =
      getAttribute(root, 'data-button-ms', '') !== '' ||
      getAttribute(root, 'data-travel-ms', '') !== '' ||
      getAttribute(root, 'data-stagger-ms', '') !== '';

    if (legacy > 0 && !explicit) {
      var natural = t.buttonMs + t.holdMs + this.beltSpan(t);
      var scale = clamp((legacy * 1000) / natural, 0.4, 3);
      t.buttonMs *= scale;
      t.holdMs *= scale;
      t.travelMs *= scale;
      t.wipeMs *= scale;
      t.staggerMs *= scale;
    }
  };

  LogoCanvas.prototype.revealOrder = function (index) {
    if (this.style === 'blades') return Math.abs(index - this.powerIndex) - 1;
    return index;
  };

  LogoCanvas.prototype.beltSpan = function (t) {
    var timing = t || this.timing;
    if (this.style === 'chevron') return timing.travelMs;

    var maxOrder = 0;
    var index;

    for (index = 0; index < this.text.length; index += 1) {
      if (index === this.powerIndex) continue;
      maxOrder = Math.max(maxOrder, this.revealOrder(index));
    }

    return maxOrder * timing.staggerMs + timing.wipeMs;
  };

  LogoCanvas.prototype.totalDuration = function () {
    return this.timing.buttonMs + this.timing.holdMs + this.beltSpan();
  };

  LogoCanvas.prototype.fontDeclaration = function (size) {
    return this.fontWeight + ' ' + size + 'px "' + this.fontFamily + '", sans-serif';
  };

  LogoCanvas.prototype.resize = function () {
    var bounds = this.canvas.getBoundingClientRect();
    this.pixelRatio = clamp(window.devicePixelRatio || 1, 1, 3);
    this.canvas.width = Math.max(1, Math.round(bounds.width * this.pixelRatio));
    this.canvas.height = Math.max(1, Math.round(bounds.height * this.pixelRatio));
  };

  LogoCanvas.prototype.measureLayout = function (fontSize) {
    var context = this.context;
    var advances = [];
    var centers = [];
    var total = 0;
    var index;
    var width;

    context.font = this.fontDeclaration(fontSize);
    var capHeight = context.measureText('E').actualBoundingBoxAscent || 0.72 * fontSize;

    for (index = 0; index < this.text.length; index += 1) {
      width = context.measureText(this.text[index]).width;
      if (index === this.powerIndex) width = Math.max(width, 1.09 * capHeight);
      advances.push(width);
      centers.push(total + width / 2);
      total += width;
    }

    return {
      size: fontSize,
      advances: advances,
      centers: centers,
      total: total,
      capHeight: capHeight
    };
  };

  LogoCanvas.prototype.layout = function (availableWidth) {
    var fontSize = this.capHeight / 0.72;
    var layout = this.measureLayout(fontSize);
    var usableWidth = Math.max(1, availableWidth - 20);

    if (layout.total > usableWidth) {
      fontSize *= usableWidth / layout.total;
      layout = this.measureLayout(fontSize);
      layout.size = fontSize;
    }

    return layout;
  };

  // Near constant speed. A belt runs and then it is gone, it does not ease out.
  // The only shaping is a short ramp, matched in value and slope at the joint
  // so there is no visible kink where the two sections meet.
  LogoCanvas.prototype.beltPosition = function (t) {
    var lead = this.timing.leadIn;
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    if (lead <= 0) return t;

    var span = 1 - lead / 2;
    if (t < lead) return (t * t) / (2 * lead) / span;
    return (t - lead / 2) / span;
  };

  // The run starts entirely off the left edge and finishes entirely off the
  // right. The reveal boundary is its LEADING edge: a letter resolves the
  // moment the front slat reaches it, and the rest of the run then rides over
  // that finished letter. Dark slats on a dark screen are invisible, dark slats
  // over resolved white letters are the striping from the client's storyboard.
  LogoCanvas.prototype.beltMetrics = function (width, elapsed) {
    var timing = this.timing;
    var pitch = width * timing.pitch;
    var packWidth = timing.lines * pitch;
    var distance = width + packWidth;
    var tail =
      -packWidth + this.beltPosition(clamp(elapsed / timing.travelMs, 0, 1)) * distance;

    return {
      pitch: pitch,
      packWidth: packWidth,
      tail: tail,
      front: elapsed >= timing.travelMs ? width : clamp(tail + packWidth, 0, width)
    };
  };

  LogoCanvas.prototype.eachLetter = function (layout, left, baseline, draw) {
    var index;
    for (index = 0; index < this.text.length; index += 1) {
      if (index === this.powerIndex) continue;
      draw(this.text[index], left + layout.centers[index] - layout.advances[index] / 2, baseline);
    }
  };

  LogoCanvas.prototype.paintOutline = function (layout, left, baseline) {
    var context = this.context;
    if (this.timing.outlineAlpha <= 0) return;

    context.save();
    context.globalAlpha = this.timing.outlineAlpha;
    context.strokeStyle = OUTLINE_INK;
    context.lineWidth = Math.max(0.8, layout.capHeight * this.timing.outlineWidth);
    this.eachLetter(layout, left, baseline, function (ch, x, y) {
      context.strokeText(ch, x, y);
    });
    context.restore();
  };

  // Gradient gating rather than a hard clip. Canvas clamps a gradient to its end
  // stops, so everything behind the feather band is fully opaque and everything
  // ahead of the front is fully clear, with a soft handover between.
  LogoCanvas.prototype.paintSolid = function (layout, left, baseline, width, front, ink) {
    var context = this.context;
    if (front <= 0) return;

    var feather = Math.max(1, width * this.timing.feather);
    var fade = context.createLinearGradient(front - feather, 0, front, 0);
    fade.addColorStop(0, ink);
    fade.addColorStop(1, 'rgba(255,255,255,0)');

    context.save();
    context.fillStyle = fade;
    this.eachLetter(layout, left, baseline, function (ch, x, y) {
      context.fillText(ch, x, y);
    });
    context.restore();
  };

  // Each slat carries the same notch as the left edge of the E in the wordmark:
  // a symmetrical chevron with its apex at mid height pointing RIGHT, in the
  // travel direction. Both edges are cut so thickness stays constant.
  LogoCanvas.prototype.chevronSlat = function (x, top, slatWidth, slatHeight) {
    var context = this.context;
    var half = slatHeight / 2;
    var midY = top + half;
    var bottom = top + slatHeight;
    var inset = half * this.timing.chevron;

    context.beginPath();
    context.moveTo(x, top);
    context.lineTo(x + slatWidth, top);
    context.lineTo(x + slatWidth + inset, midY);
    context.lineTo(x + slatWidth, bottom);
    context.lineTo(x, bottom);
    context.lineTo(x + inset, midY);
    context.closePath();
    context.fill();
  };

  LogoCanvas.prototype.paintChevronRun = function (
    width,
    height,
    elapsed,
    buttonX,
    buttonY,
    buttonRadius
  ) {
    var context = this.context;
    var timing = this.timing;
    if (elapsed <= 0 || elapsed >= timing.travelMs) return;

    var m = this.beltMetrics(width, elapsed);
    var slatHeight = height * timing.height;
    var top = (height - slatHeight) / 2;
    var index;

    context.save();

    /* Even-odd against a full bleed rect leaves a void at the mark, so no slat
       is ever rasterised over the power button. The button owns its own stage. */
    context.beginPath();
    context.rect(0, 0, width, height);
    context.arc(buttonX, buttonY, buttonRadius * timing.buttonClear, 0, Math.PI * 2);
    context.clip('evenodd');

    for (index = 0; index < timing.lines; index += 1) {
      /* Index 0 is the tail, the last index leads. The run is heaviest at its
         leading edge and tapers backward in both width and opacity, so it reads
         as thick to thin and the wordmark is clean once it has gone. */
      var lead = timing.lines === 1 ? 1 : index / (timing.lines - 1);
      var duty = timing.dutyTail + (timing.duty - timing.dutyTail) * lead;
      var slatWidth = m.pitch * duty;
      var alpha = timing.slatAlpha * (1 - timing.trail * (1 - lead));

      /* Centred in the pitch slot so the taper eats in from both sides rather
         than sliding the slats leftward as they thin. */
      var x = m.tail + index * m.pitch + (m.pitch * timing.duty - slatWidth) / 2;

      if (x > width || x + slatWidth < 0 || alpha <= 0.01 || slatWidth < 0.4) continue;

      context.fillStyle = rgba(this.slatColor, alpha);
      this.chevronSlat(x, top, slatWidth, slatHeight);
    }

    context.restore();
  };

  LogoCanvas.prototype.paintPowerButton = function (centerX, centerY, capHeight, progress) {
    var context = this.context;
    var timing = this.timing;
    var outerRadius = capHeight * FACE_RATIO;
    var ringWidth = Math.max(2.2, capHeight * RING_RATIO);
    var ringRadius = outerRadius - ringWidth / 2;
    var faceRadius = ringRadius - ringWidth / 2;
    var glyphRadius = faceRadius * GLYPH_RATIO;

    context.save();
    context.lineCap = 'round';

    context.fillStyle = FACE_FILL;
    context.beginPath();
    context.arc(centerX, centerY, faceRadius, 0, Math.PI * 2);
    context.fill();

    var ringGradient = context.createLinearGradient(
      centerX - outerRadius,
      centerY - outerRadius,
      centerX + outerRadius,
      centerY + outerRadius
    );
    ringGradient.addColorStop(0, '#FAFBF9');
    ringGradient.addColorStop(0.45, '#82897F');
    ringGradient.addColorStop(0.7, '#3E443F');
    ringGradient.addColorStop(1, '#F6F7F5');
    context.strokeStyle = ringGradient;
    context.lineWidth = ringWidth;
    context.beginPath();
    context.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
    context.stroke();

    /* Colour interpolation plus a stroke weight ramp. No shadow blur, no
       composite mode, no secondary fill. Nothing here emits light. The weight
       carries a mid transition bulge so the glyph reads as drawing current
       before it settles at its final section. */
    var eased = easeInOutCubic(progress);
    var bulge = timing.powerSettle * Math.sin(Math.PI * clamp(progress, 0, 1));
    var strokeRatio =
      timing.powerStrokeStart +
      (timing.powerStrokeEnd - timing.powerStrokeStart) * (eased + bulge);

    context.strokeStyle = mixColor(this.metal, this.accent, eased);
    context.lineWidth = Math.max(1.3, capHeight * strokeRatio);
    context.beginPath();
    context.arc(centerX, centerY + glyphRadius * 0.12, glyphRadius, ARC_START, ARC_END);
    context.stroke();
    context.beginPath();
    context.moveTo(centerX, centerY - glyphRadius * 1.1);
    context.lineTo(centerX, centerY + glyphRadius * 0.1);
    context.stroke();

    context.restore();
  };

  LogoCanvas.prototype.clipSweep = function (x0, x1, top, bottom) {
    var context = this.context;
    context.beginPath();
    context.rect(Math.min(x0, x1), top, Math.abs(x1 - x0), bottom - top);
    context.clip();
  };

  LogoCanvas.prototype.paintLegacyLetter = function (
    layout,
    left,
    baseline,
    index,
    progress,
    ink,
    toLeft
  ) {
    if (progress <= 0) return;

    var context = this.context;
    var letterLeft = left + layout.centers[index] - layout.advances[index] / 2;
    var advance = layout.advances[index];
    var pad = layout.capHeight * 0.06;
    var top = baseline - layout.capHeight * 1.25;
    var bottom = baseline + layout.capHeight * 0.3;
    var travel = advance + pad * 2;
    var front = progress >= 1 ? travel : progress * travel;
    var x0;
    var x1;

    context.save();
    if (toLeft) {
      x1 = letterLeft + advance + pad;
      x0 = x1 - front;
    } else {
      x0 = letterLeft - pad;
      x1 = x0 + front;
    }
    this.clipSweep(x0, x1, top, bottom);
    context.fillStyle = ink;
    context.fillText(this.text[index], letterLeft, baseline);
    context.restore();
  };

  LogoCanvas.prototype.paintLegacyBelt = function (layout, left, baseline, elapsed, span) {
    var context = this.context;
    var timing = this.timing;

    if (this.style === 'blades') {
      var t = clamp(elapsed / span, 0, 1);
      var lineLength = layout.capHeight * BLADE_LENGTH_RATIO;
      var thick = Math.max(2.4, layout.capHeight * 0.11);
      var thin = Math.max(0.45, layout.capHeight * 0.014);
      var originX = left + layout.centers[this.powerIndex];
      var accent = this.accent;
      var index;

      context.save();
      context.beginPath();
      context.rect(
        left - lineLength * 0.2,
        baseline - layout.capHeight * 1.25,
        layout.total + lineLength * 1.4,
        layout.capHeight * 1.55
      );
      context.clip();

      for (index = 0; index < BLADE_OFFSETS.length; index += 1) {
        var delay = BLADE_STAGGER * index;
        var local = clamp((t - delay) / (1 - delay), 0, 1);
        var opacity = Math.max(0.18, 0.92 * Math.sin(Math.PI * easeInOutCubic(local)));
        var centerY =
          baseline - layout.capHeight / 2 + layout.capHeight * BLADE_OFFSETS[index];

        context.fillStyle = rgba(accent, opacity);

        [1, -1].forEach(function (direction) {
          var frontX = originX + direction * local * layout.total;
          var rearX = frontX - direction * lineLength;

          context.beginPath();
          context.moveTo(rearX, centerY - thick / 2);
          context.lineTo(frontX, centerY - thin / 2);
          context.lineTo(frontX, centerY + thin / 2);
          context.lineTo(rearX, centerY + thick / 2);
          context.closePath();
          context.fill();
        });
      }

      context.restore();
      return;
    }

    var top = baseline - layout.capHeight * 1.18;
    var bottom = baseline + layout.capHeight * 0.22;
    var midY = (top + bottom) / 2;
    var fullHeight = bottom - top;
    var gap = layout.capHeight * timing.barGap;
    var startWidth = layout.capHeight * timing.barWidth;
    var orderPos = elapsed / timing.staggerMs;
    var averageAdvance = Math.max(1, layout.total / this.text.length);
    var i;

    context.save();
    context.beginPath();
    context.rect(left - gap * timing.bars, top, layout.total + gap * timing.bars * 2, fullHeight);
    context.clip();

    for (i = 0; i < timing.bars; i += 1) {
      var pos = orderPos - i * (gap / averageAdvance);
      var k = Math.floor(pos);
      var frac = pos - k;
      var a =
        k <= 0
          ? left
          : k >= this.text.length
            ? left + layout.total
            : left + layout.centers[k] - layout.advances[k] / 2;
      var b =
        k + 1 <= 0
          ? left
          : k + 1 >= this.text.length
            ? left + layout.total
            : left + layout.centers[k + 1] - layout.advances[k + 1] / 2;
      var x = a + (b - a) * frac;

      if (x < left - gap || x > left + layout.total + gap) continue;

      var local2 = clamp((x - left) / Math.max(1, layout.total), 0, 1);
      var width2 = Math.max(0.6, startWidth * (1 - (1 - timing.taperEnd) * local2));
      var height2 = fullHeight * (1 - timing.heightTaper * local2);
      var alpha2 = clamp(orderPos / 0.9, 0, 1) * (1 - 0.22 * local2) * (1 - i * 0.2);

      if (alpha2 <= 0.01) continue;

      context.fillStyle = rgba(this.accent, alpha2);
      context.fillRect(x - width2 / 2, midY - height2 / 2, width2, height2);
    }

    context.restore();
  };

  LogoCanvas.prototype.paint = function (elapsed) {
    var context = this.context;
    var timing = this.timing;
    var width = this.canvas.width / this.pixelRatio;
    var height = this.canvas.height / this.pixelRatio;
    var layout = this.layout(width);
    var baseline = height / 2 + layout.capHeight / 2;
    var left = (width - layout.total) / 2;
    var ink = this.adaptiveInk ? window.getComputedStyle(this.root).color : DEFAULT_INK;
    var index;

    var buttonProgress = clamp(elapsed / timing.buttonMs, 0, 1);
    var beltElapsed = elapsed - (timing.buttonMs + timing.holdMs);

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);

    if (!this.transparent) {
      var background = context.createLinearGradient(0, 0, 0, height);
      background.addColorStop(0, SCREEN_TOP);
      background.addColorStop(1, SCREEN_BOTTOM);
      context.fillStyle = background;
      context.fillRect(0, 0, width, height);
    }

    context.font = this.fontDeclaration(layout.size);
    context.textBaseline = 'alphabetic';
    context.textAlign = 'left';
    context.lineJoin = 'round';

    var buttonX = left + layout.centers[this.powerIndex] + layout.capHeight * 0.05;
    var buttonY = baseline - layout.capHeight / 2;
    var buttonRadius = layout.capHeight * FACE_RATIO;

    this.paintOutline(layout, left, baseline);

    if (this.style === 'chevron') {
      var front = beltElapsed > 0 ? this.beltMetrics(width, beltElapsed).front : 0;
      this.paintSolid(layout, left, baseline, width, front, ink);
      this.paintChevronRun(width, height, beltElapsed, buttonX, buttonY, buttonRadius);
    } else {
      var span = this.beltSpan();

      if (beltElapsed > 0) {
        for (index = 0; index < this.text.length; index += 1) {
          if (index === this.powerIndex) continue;
          var offset = this.revealOrder(index) * timing.staggerMs;
          var letterProgress = smoothstep((beltElapsed - offset) / timing.wipeMs);
          this.paintLegacyLetter(
            layout,
            left,
            baseline,
            index,
            letterProgress,
            ink,
            this.style === 'blades' && index < this.powerIndex
          );
        }

        if (beltElapsed < span) {
          this.paintLegacyBelt(layout, left, baseline, beltElapsed, span);
        }
      }
    }

    this.paintPowerButton(buttonX, buttonY, layout.capHeight, buttonProgress);
  };

  LogoCanvas.prototype.stop = function () {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.running = false;
  };

  LogoCanvas.prototype.shouldAnimate = function () {
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.enabled || this.skipBoot || reduceMotion) return false;
    if (window.Shopify && window.Shopify.designMode) return true;
    if (this.frequency === 'never') return false;
    if (this.frequency === 'load') return true;

    try {
      return window.sessionStorage.getItem(STORAGE_KEY) !== 'true';
    } catch (error) {
      return true;
    }
  };

  LogoCanvas.prototype.render = function () {
    var instance = this;

    this.primed = true;
    this.stop();
    this.resize();
    this.readTiming();

    if (!this.shouldAnimate()) {
      this.paint(Number.MAX_SAFE_INTEGER);
      return;
    }

    var total = this.totalDuration();
    var startedAt = performance.now();

    this.running = true;
    this.paint(0);

    this.raf = window.requestAnimationFrame(function frame(now) {
      var elapsed = now - startedAt;

      instance.paint(elapsed);

      if (elapsed < total) {
        instance.raf = window.requestAnimationFrame(frame);
      } else {
        instance.paint(total);
        instance.running = false;
        instance.raf = 0;
        try {
          window.sessionStorage.setItem(STORAGE_KEY, 'true');
        } catch (error) {
          // Storage may be unavailable in privacy modes.
        }
      }
    });
  };

  // Kept for the ROI print path, which forces the mark to its finished state
  // before rendering a PDF.
  LogoCanvas.prototype.renderStatic = function () {
    this.primed = true;
    this.stop();
    this.resize();
    this.readTiming();
    this.paint(Number.MAX_SAFE_INTEGER);
  };

  LogoCanvas.prototype.size = function () {
    this.resize();
    if (this.primed && !this.running) this.paint(Number.MAX_SAFE_INTEGER);
  };

  LogoCanvas.prototype.renderAfterFontLoads = function () {
    var instance = this;
    var declaration = this.fontWeight + ' 32px "' + this.fontFamily + '"';

    if (document.fonts && typeof document.fonts.load === 'function') {
      document.fonts
        .load(declaration)
        .then(function () {
          return document.fonts.ready;
        })
        .then(function () {
          instance.render();
        })
        .catch(function () {
          instance.render();
        });
    } else {
      this.render();
    }
  };

  function mountFooterLogos() {
    var links = document.querySelectorAll(
      '.eds-footer__brand-name:not([data-eds-footer-logo-enhanced])'
    );
    if (!links.length) return;

    var headerLogo = document.querySelector('#header-group [data-eds-canvas-logo]');
    var fontFamily = getAttribute(headerLogo, 'data-font', 'Unbounded');
    var fontWeight = getAttribute(headerLogo, 'data-font-weight', '700');
    var words = getAttribute(headerLogo, 'data-words', 'ECO,DRIVE,SMART');
    var accent = getAttribute(headerLogo, 'data-accent', DEFAULT_ACCENT);
    var headerCap = getNumber(headerLogo, 'data-cap', 25);
    var footerCap = clamp(headerCap, 24, 28);

    if (!document.getElementById(FOOTER_STYLE_ID)) {
      var style = document.createElement('style');
      style.id = FOOTER_STYLE_ID;
      style.textContent =
        '.eds-footer__logo-link--canvas{display:inline-flex;width:fit-content;max-width:100%;line-height:0}' +
        '.eds-footer__canvas-logo{display:block;width:clamp(220px,var(--eds-footer-logo-w,220px),300px);max-width:100%;height:56px;color:var(--eds-footer-text,#183123)}' +
        '.eds-footer__canvas-logo canvas{display:block;width:100%;height:100%}';
      document.head.appendChild(style);
    }

    Array.prototype.forEach.call(links, function (link) {
      var wrapper = document.createElement('span');
      var canvas = document.createElement('canvas');

      link.setAttribute('data-eds-footer-logo-enhanced', 'true');
      link.className = 'eds-footer__logo-link eds-footer__logo-link--canvas';
      link.textContent = '';

      wrapper.className = 'eds-canvas-logo eds-footer__canvas-logo';
      wrapper.setAttribute('data-eds-canvas-logo', '');
      wrapper.setAttribute('data-words', words);
      wrapper.setAttribute('data-cap', String(footerCap));
      wrapper.setAttribute('data-accent', accent);
      wrapper.setAttribute('data-font', fontFamily);
      wrapper.setAttribute('data-font-weight', fontWeight);
      wrapper.setAttribute('data-surface', 'transparent');
      wrapper.setAttribute('data-adaptive-ink', 'true');
      wrapper.setAttribute('data-skip-boot', 'true');

      canvas.setAttribute('data-canvas', '');
      canvas.setAttribute('role', 'img');
      canvas.setAttribute('aria-label', link.getAttribute('aria-label') || 'EcoDriveSmart');

      wrapper.appendChild(canvas);
      link.appendChild(wrapper);
    });
  }

  function initOne(root) {
    if (root.__edsLogoBootFlash) return;

    var logo = new LogoCanvas(root);
    if (!logo.context) return;

    root.__edsLogoBootFlash = logo;
    root.__edsLogo = logo;
    logo.renderAfterFontLoads();

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        logo.resize();
        if (logo.primed && !logo.running) logo.paint(Number.MAX_SAFE_INTEGER);
      }).observe(logo.canvas);
    }
  }

  function initAll(scope) {
    var searchRoot = scope && scope.querySelectorAll ? scope : document;
    var roots = [];

    mountFooterLogos();

    if (searchRoot.matches && searchRoot.matches('[data-eds-canvas-logo]')) {
      roots.push(searchRoot);
    }

    Array.prototype.forEach.call(
      searchRoot.querySelectorAll('[data-eds-canvas-logo]'),
      function (root) {
        roots.push(root);
      }
    );

    roots.forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  window.EDSLogoCanvasBootFlash = {
    initAll: initAll,
    initOne: initOne,
    mountFooterLogos: mountFooterLogos
  };
  window.EDSLogoCanvas = window.EDSLogoCanvasBootFlash;
})(window, document);
